import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {RootState} from '../../app/rootReducer'
import {
    fetchAllModelsWithActivePromotions,
    fetchCarBySlug,
} from '../../app/thunk'
import SingleModelInterface, {Promotion} from '../../types/singleModelInterface'
import Brands from '../../utils/brand/brand'
import CustomerTypes from '../../utils/customerTypes/customerTypes'
import {AuthServiceToken} from '../../types/authServiceToken'

export interface InitialStateInterface {
    apiQueryParams: ''
    backendUrl: ''
    baseUrl: ''
    brand: Brands
    carCallOver: false
    carsByCustomerTypeNumber: 0
    customerType: CustomerTypes.PRIVATE
    filteredModels: SingleModelInterface[]
    isLegalNotesOpen: true
    isShowroom: false
    legalNotesText: ''
    loading: boolean
    modelFromCms: ''
    modelsWithActivePromotions: SingleModelInterface[]
    numberOfModelsWithActivePromotions: 0
    previewAPIToken: ''
    selectedCarNames: string[]
    selectedCarTypes: string[]
    selectedEngineTypes: string[]
    selectedCarModelTypes: string[]
    selectedPromotion: Promotion | null
    singleCar: SingleModelInterface
    trackingBrand: ''
    logged: boolean | undefined
    showCouponLayer: boolean
    campaignId: string
    showRFQForm: boolean
    fivePlusLayerPath: string
}

const initialState: InitialStateInterface = {
    apiQueryParams: '',
    backendUrl: '',
    baseUrl: '',
    brand: '' as Brands,
    carCallOver: false,
    carsByCustomerTypeNumber: 0,
    customerType: CustomerTypes.PRIVATE,
    filteredModels: [] as SingleModelInterface[],
    isLegalNotesOpen: true,
    isShowroom: false,
    legalNotesText: '',
    loading: true,
    modelFromCms: '',
    modelsWithActivePromotions: [],
    numberOfModelsWithActivePromotions: 0,
    previewAPIToken: '',
    selectedCarNames: [],
    selectedCarTypes: [],
    selectedEngineTypes: [],
    selectedCarModelTypes: [],
    selectedPromotion: null,
    singleCar: {} as SingleModelInterface,
    trackingBrand: '',
    logged: undefined,
    showCouponLayer: false,
    campaignId: '',
    showRFQForm: false,
    fivePlusLayerPath: '',
}

export const generalSlice = createSlice({
    name: 'general',
    initialState,
    reducers: {
        setLoading(state: RootState, action: PayloadAction<boolean>): void {
            state.loading = action.payload
        },
        setIsShowroom(state: RootState, action: PayloadAction<boolean>): void {
            state.isShowroom = action.payload
        },
        setSelectedCarTypes(
            state: RootState,
            action: PayloadAction<string[]>
        ): void {
            state.selectedCarTypes = action.payload
        },
        setSelectedEngineTypes(
            state: RootState,
            action: PayloadAction<string[]>
        ): void {
            state.selectedEngineTypes = action.payload
        },
        setSelectedCarModelTypes(
            state: RootState,
            action: PayloadAction<string[]>
        ): void {
            state.selectedCarModelTypes = action.payload
        },
        setSelectedCarNames(
            state: RootState,
            action: PayloadAction<string[]>
        ): void {
            state.selectedCarNames = action.payload
        },
        setSelectedPromotion(
            state: RootState,
            action: PayloadAction<{
                id: number
                title: string
                motorization: string | undefined
            } | null>
        ): void {
            state.selectedPromotion = action.payload
        },
        setShowCouponLayer(
            state: RootState,
            action: PayloadAction<boolean>
        ): void {
            state.showCouponLayer = action.payload
        },
        setFilteredModels(
            state: RootState,
            action: PayloadAction<SingleModelInterface[]>
        ): void {
            state.filteredModels = action.payload
        },
        setIsLegalNotesOpen(
            state: RootState,
            action: PayloadAction<boolean>
        ): void {
            state.isLegalNotesOpen = action.payload
        },
        setLegalNotesText(
            state: RootState,
            action: PayloadAction<string>
        ): void {
            state.legalNotesText = action.payload
        },
        setBackendUrl(state: RootState, action: PayloadAction<string>): void {
            state.backendUrl = action.payload
        },
        setApiQueryParams(
            state: RootState,
            action: PayloadAction<string>
        ): void {
            state.apiQueryParams = action.payload
        },
        setPreviewAPIToken(
            state: RootState,
            action: PayloadAction<string>
        ): void {
            state.previewAPIToken = action.payload
        },
        setTrackingBrand(
            state: RootState,
            action: PayloadAction<string>
        ): void {
            state.trackingBrand = action.payload
        },
        setBrand(state: RootState, action: PayloadAction<string>): void {
            state.brand = action.payload
        },
        setModelFromCms(
            state: RootState,
            action: PayloadAction<string | undefined>
        ): void {
            state.modelFromCms =
                typeof action.payload === 'undefined' ? '' : action.payload
        },
        setSingleCar(
            state: RootState,
            action: PayloadAction<SingleModelInterface | {}>
        ): void {
            state.singleCar = action.payload
        },
        setBaseUrl(state: RootState, action: PayloadAction<string>): void {
            state.baseUrl = action.payload
        },
        setCustomerType(
            state: RootState,
            action: PayloadAction<CustomerTypes>
        ): void {
            state.customerType = action.payload
        },
        setCarsByCustomerTypeNumber(
            state: RootState,
            action: PayloadAction<number>
        ): void {
            state.carsByCustomerTypeNumber = action.payload
        },
        setLogged(state: RootState, action: PayloadAction<boolean>): void {
            state.logged = action.payload
        },
        setAuthServiceToken(
            state: RootState,
            action: PayloadAction<AuthServiceToken>
        ): void {
            state.authServiceToken = action.payload
        },
        setCampaignId(state: RootState, action: PayloadAction<string>): void {
            state.campaignId = action.payload
        },
        setShowRFQForm(
            state: RootState,
            action: PayloadAction<boolean | undefined>
        ): void {
            state.showRFQForm = action.payload
        },
        setFivePlusLayerPath(
            state: RootState,
            action: PayloadAction<string | undefined>
        ): void {
            state.fivePlusLayerPath =
                typeof action.payload === 'undefined' ? '' : action.payload
        },
    },
    extraReducers: (builder) => {
        builder.addCase(
            fetchAllModelsWithActivePromotions.fulfilled,
            (state: RootState, action) => {
                const models = action.payload

                state.carCallOver = true
                state.modelsWithActivePromotions = models
                state.filteredModels = state.modelsWithActivePromotions
                state.numberOfModelsWithActivePromotions = models.length
            }
        )
        builder.addCase(
            fetchCarBySlug.fulfilled,
            (state: RootState, action) => {
                state.singleCar = action.payload
                state.carCallOver = true
            }
        )
    },
})

export const {
    setLoading,
    setSelectedCarTypes,
    setSelectedEngineTypes,
    setSelectedCarModelTypes,
    setSelectedCarNames,
    setFilteredModels,
    setIsShowroom,
    setIsLegalNotesOpen,
    setLegalNotesText,
    setTrackingBrand,
    setBackendUrl,
    setPreviewAPIToken,
    setApiQueryParams,
    setBrand,
    setModelFromCms,
    setSingleCar,
    setBaseUrl,
    setCustomerType,
    setCarsByCustomerTypeNumber,
    setLogged,
    setAuthServiceToken,
    setShowRFQForm,
    setSelectedPromotion,
    setCampaignId,
    setShowCouponLayer,
    setFivePlusLayerPath,
} = generalSlice.actions

export default generalSlice.reducer
